// Per rule: code-comments-required (ai.knowledge/global) — why, not what.
// Per rule: reference-rules-in-comments (ai.knowledge/global) — trace decisions to rules.
// Per rule: stay-in-scope (ai.knowledge/global) — RTMP billing-limit helpers only.
// See knowledge: use-canonical-logger-not-console (ai.knowledge/studio.client) — no console here.

import type { T_Maybe } from '@tryiris-ai/js-types';
import type { State } from '@tryiris-ai/common.protojs/agent/v1/agent';

/** Phrase present on encoder/state-sync billing-limit failures. */
export const BILLING_LIMIT_ERROR_TEXT = 'cloud usage limit reached';

/** Encoder/command wire message — scanners must keep matching this string. */
export const BILLING_LIMIT_RTMP_ERROR = 'RTMP disabled: cloud usage limit reached';

/** Destination-card prompt body. Title stays "Cloud usage limit reached". */
export const CLOUD_USAGE_LIMIT_MESSAGE =
  'RTMP streaming is disabled until plan usage is available or overage is enabled.';

/** Store-thrown message; includes the scan phrase so the card catch still classifies it. */
export const CLOUD_USAGE_LIMIT_ERROR_MESSAGE = `Cloud usage limit reached. ${CLOUD_USAGE_LIMIT_MESSAGE}`;

// Bound recursion so a hostile/cyclic payload cannot blow the stack.
const MAX_SCAN_DEPTH = 8;

const containsBillingLimitText = (value: string): boolean =>
  value.toLowerCase().includes(BILLING_LIMIT_ERROR_TEXT);

const scanBillingLimitError = (value: unknown, seen: WeakSet<object>, depth: number): boolean => {
  if (depth > MAX_SCAN_DEPTH) {
    return false;
  }

  if (typeof value === 'string') {
    return containsBillingLimitText(value);
  }

  if (value instanceof Error) {
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return (
      containsBillingLimitText(value.message) || scanBillingLimitError(value.cause, seen, depth + 1)
    );
  }

  if (!value || typeof value !== 'object') {
    return false;
  }

  if (seen.has(value)) {
    return false;
  }
  seen.add(value);

  const children = Array.isArray(value) ? value : Object.values(value);
  return children.some((item) => scanBillingLimitError(item, seen, depth + 1));
};

/**
 * Detect billing-limit failures from Error.message/cause plus nested arrays/objects.
 * Cycle-safe: WeakSet + depth cap. Does not throw on cyclic graphs.
 */
export const isBillingLimitError = (value: unknown): boolean =>
  scanBillingLimitError(value, new WeakSet<object>(), 0);

export type EncoderBillingLockSnapshot = {
  billingLocked: boolean;
  deviceIds: string[];
};

/** Pure selector for useMultiProxyStateQuery — extract encoder lock + owned devices. */
export const selectEncoderBillingLock = (state: T_Maybe<State>): EncoderBillingLockSnapshot => ({
  billingLocked: !!state?.control?.encoder?.billingLocked,
  deviceIds: Object.keys(state?.control?.device ?? {})
});

/** True when the selected device is on an agent whose encoder billing lock is set. */
export const isDeviceEncoderBillingLocked = (
  snapshots: Record<string, EncoderBillingLockSnapshot | undefined>,
  deviceId: string | undefined
): boolean => {
  if (!deviceId) {
    return false;
  }

  return Object.values(snapshots).some(
    (snapshot) => snapshot?.billingLocked === true && snapshot.deviceIds.includes(deviceId)
  );
};
